import { call, put, takeLatest } from 'redux-saga/effects';
import { getPlanBuilderBillingFrequencies } from '@/modules/AdminTools/api/billingFrequencies';
import Api from '@/modules/AdminTools/api/planBuilder';
import { createRequestSaga } from '@/redux/helpers';
import {
  removePlanAsync,
  requestPlanAsync,
  requestPlansAsync,
  updatePlanAsync,
  createPlanAsync,
  massUpdateAsync,
  requestBillingFrequenciesAsync,
} from './actions';
import { addErrorAction } from '@/redux/errors';
import { addToastsAction } from '@/redux/toasts';
import { mapMassUpdateToast } from '@/modules/AdminTools/api/planBuilder/mapResponseToToasts';
import { toastType } from '@/components/common';

const createOnErrorHandler = (errKey) =>
  function* onError({ errors }, { action: { payload } = {} }) {
    yield put(addErrorAction({ errKey, error: errors }));
  };

function* requestPlansSaga() {
  const response = yield call(Api.getPlans);

  yield put(requestPlansAsync.success(response));
}

function* requestPlanSaga({ payload: { id } }) {
  const response = yield call(Api.getPlan(id));

  yield put(requestPlanAsync.success(response));
}

function* updatePlanSaga({ payload: { plan } }) {
  const response = yield call(Api.updatePlan(plan.id), plan);

  if (!response?.id) {
    yield put(addToastsAction([{
      type: toastType.ERROR,
      message: 'Server failed to update plan',
      details: null,
    }]));

    return;
  }

  yield put(updatePlanAsync.success(response));

  yield put(addToastsAction([{
    type: toastType.SUCCESS,
    message: 'Updated plan successfully',
    details: null,
  }]));
}

function* massUpdateSaga({ payload }) {
  const response = yield call(Api.massUpdatePlan, payload);

  yield put(massUpdateAsync.success(response));

  yield put(addToastsAction(mapMassUpdateToast(response)));
}

function* createPlanSaga({ payload: { plan } }) {
  const response = yield call(Api.createPlan, plan);

  if (!response?.id) {
    yield put(addToastsAction([{
      type: toastType.ERROR,
      message: 'Server failed to create plan',
      details: null,
    }]));

    return;
  }

  yield put(createPlanAsync.success(response));

  yield put(addToastsAction([{
    type: toastType.SUCCESS,
    message: 'Created plan successfully',
    details: null,
  }]));
}

function* removePlanSaga({ payload: { id } }) {
  yield call(Api.removePlan(id));

  yield put(removePlanAsync.success(id));

  yield put(addToastsAction([{
    type: toastType.SUCCESS,
    message: 'Removed plan successfully',
    details: null,
  }]));
}

function* requestBillingFrequenciesSaga() {
  const response = yield call(getPlanBuilderBillingFrequencies);

  yield put(requestBillingFrequenciesAsync.success(response));
}

export function* plansWatcher() {
  yield takeLatest(
    requestPlansAsync.request.type,
    createRequestSaga(requestPlansSaga, {
      keyNew: 'plans',
      errKey: 'plans',
      write: false,
    }),
  );

  yield takeLatest(
    requestPlanAsync.request.type,
    createRequestSaga(requestPlanSaga, {
      keyNew: 'plan',
      errKey: 'plan',
      write: false,
    }),
  );

  yield takeLatest(
    updatePlanAsync.request.type,
    createRequestSaga(updatePlanSaga, {
      keyNew: 'updatePlan',
      errKey: 'updatePlan',
      write: false,
      onError: createOnErrorHandler('updatePlan'),
    }),
  );

  yield takeLatest(
    massUpdateAsync.request.type,
    createRequestSaga(massUpdateSaga, {
      keyNew: 'updatePlan',
      errKey: 'updatePlan',
      write: false,
      onError: createOnErrorHandler('updatePlan'),
    }),
  );

  yield takeLatest(
    createPlanAsync.request.type,
    createRequestSaga(createPlanSaga, {
      keyNew: 'updatePlan',
      errKey: 'updatePlan',
      write: false,
      onError: createOnErrorHandler('updatePlan'),
    }),
  );

  yield takeLatest(
    removePlanAsync.request.type,
    createRequestSaga(removePlanSaga, {
      keyNew: 'plan',
      errKey: 'plan',
      write: false,
    }),
  );

  yield takeLatest(
    requestBillingFrequenciesAsync.request.type,
    createRequestSaga(requestBillingFrequenciesSaga, {
      keyNew: 'billingFrequencies',
      errKey: 'billingFrequencies',
      write: false,
    }),
  );
}
